import { IFullCardData } from "domains/cashless/aggregates/ICardCashless";
import { Order } from "domains/order/agreggates/order/Order";
import { TransactionType } from "domains/order/agreggates/payment/transactionType";
import { Kiosk } from "domains/kiosk/aggregates/Kiosk";
import { Payment } from "modules/order/domain/models/Payment";
import { IOrderProviderRef } from "modules/order/presentation/OrderProvider";
import { ICustomer } from "modules/order/domain/models/ICustomer";
import React from "react";

/**
 * Status/etapas do fluxo de recarga offline V2.
 * Ordem típica: WAITING_CARD_READ → (se fechado: WAITING_CUSTOMER_DATA →) INSERT_RECHARGE_VALUE
 * → PROCESSING_PAYMENT → WAITING_CARD_WRITE → WRITING → SUCCESS ou ERROR.
 */
export enum OfflineRechargeStep {
  /** Aguardando aproximação do cartão para leitura */
  WAITING_CARD_READ = "WAITING_CARD_READ",
  /** Aguardando dados do cliente (apenas quando conta fechada / abertura) */
  WAITING_CUSTOMER_DATA = "WAITING_CUSTOMER_DATA",
  /** Inserir valor da recarga */
  INSERT_RECHARGE_VALUE = "INSERT_RECHARGE_VALUE",
  /** Processando pagamento (PIX, débito, etc.) */
  PROCESSING_PAYMENT = "PROCESSING_PAYMENT",
  /** Aguardando aproximação do cartão para escrita do saldo */
  WAITING_CARD_WRITE = "WAITING_CARD_WRITE",
  /** Escrevendo no cartão */
  WRITING = "WRITING",
  /** Erro (exibir toast ou tela de erro) */
  ERROR = "ERROR",
  /** Pendência de recarga */
  PENDING_RECHARGE = "PENDING_RECHARGE",
  /** Pendência de abertura de conta */
  PENDING_OPEN_ACCOUNT = "PENDING_OPEN_ACCOUNT",
}

/** Chaves i18n para o rótulo de cada etapa (usar com t()). */
export const OfflineRechargeStepLabels: Record<OfflineRechargeStep, string> = {
  [OfflineRechargeStep.WAITING_CARD_READ]: "offline_recharge_v2.steps.waiting_card_read",
  [OfflineRechargeStep.WAITING_CUSTOMER_DATA]: "offline_recharge_v2.steps.waiting_customer_data",
  [OfflineRechargeStep.INSERT_RECHARGE_VALUE]: "offline_recharge_v2.steps.insert_recharge_value",
  [OfflineRechargeStep.PROCESSING_PAYMENT]: "offline_recharge_v2.steps.processing_payment",
  [OfflineRechargeStep.WAITING_CARD_WRITE]: "offline_recharge_v2.steps.waiting_card_write",
  [OfflineRechargeStep.WRITING]: "offline_recharge_v2.steps.writing",
  [OfflineRechargeStep.ERROR]: "offline_recharge_v2.steps.error",
  [OfflineRechargeStep.PENDING_RECHARGE]: "offline_recharge_v2.steps.pending_recharge",
  [OfflineRechargeStep.PENDING_OPEN_ACCOUNT]: "offline_recharge_v2.steps.pending_open_account",
};

export interface UseOfflineRechargeV2Params {
  kiosk: Kiosk | null | undefined;
  orderProvider: React.RefObject<IOrderProviderRef>;
  /** Abre o modal de identificação do cliente (conta fechada). Retorna o cliente ou null. */
  openCustomerForClosedCard?: (kiosk: Kiosk) => Promise<ICustomer | null>;
  /** Fecha o modal de documento (ex.: quando há recarga pendente e deve seguir em background). */
  closeCustomerModal?: () => void;
}

/** Resultado da leitura do cartão offline (e estado do cartão na página). */
export interface IOfflineCardState {
  cardData?: IFullCardData;
  isClosed?: boolean;
  uid: string;
}

export interface IUseOfflineRecharge {
  step: OfflineRechargeStep;
  offlineCard: IOfflineCardState | null;
  rechargeValueCents: number;
  activationDisplayValue: number;
  showRetryReadButton: boolean;
  currentStepIndex: number;
  handleGoBack: () => void;
  onRetryRead: () => void;
  /** Confirma o CPF na etapa WAITING_CUSTOMER_DATA e avança para INSERT_RECHARGE_VALUE. */
  onSubmitCpf: (document: string | null) => void;
  onSubmitRechargeValue: (
    params: IOnSubmitRechargeValueParams
  ) => Promise<IOnValidateRechargeValue> | IOnValidateRechargeValue;
  addQuickValue: (reais: number) => void;
  handleKeyPress: (key: string) => void;
  onClearValue: () => void;
  getDocumentDisplay: (data: IFullCardData) => string;
  resetFlow: () => void;
}

/** Parâmetros para iniciar o pagamento da recarga offline */
export interface IPaymentCashlessOfflineParams {
  /** Valor da recarga em centavos */
  rechargeValueCents: number;
  /** Dados do cartão lido */
  cardData: IFullCardData;
  /** true = abertura de conta (cartão fechado), false = recarga (cartão já aberto) */
  isClosed: boolean;
  /** Tipo de transação (pix, débito, crédito, etc.) */
  transactionType: TransactionType;
  /** Função que abre o fluxo de pagamento (ex.: orderProviderRef.current.startPayment) */
  //startPayment: (
  //  order: IOrder,
  //  flow: "Recharge" | "Payment" | "WalletRecharge",
  //  paymentType?: TransactionType
  //) => Promise<IPaymentResult>;
}

/** Resultado do pagamento da recarga offline (sucesso) */
export interface IPaymentCashlessOfflineResult {
  order?: Order;
  payment?: Payment;
  success?: boolean;
  errorMessage?: string;
}

/** Parâmetros para o submit do valor de recarga */
export interface IOnSubmitRechargeValueParams {
  /** Valor da recarga em centavos */
  rechargeValueCents: number;
  /** Dados do cartão lido */
  cardData?: IFullCardData;
  uid: string;
  /**
   * true = abertura de conta (cartão fechado): será cobrado valor de ativação + recarga;
   * apenas o valor da recarga será carregado no cartão.
   * false = conta já aberta: apenas recarga, após pagamento chama writeBalance.
   */
  isClosed: boolean;
  /** Tipo de transação (pix, débito, crédito, etc.) */
  transactionType: TransactionType;
}

/** Resultado da validação do submit do valor */
export interface IOnValidateRechargeValue {
  valid: boolean;
  errorMessage?: string;
}


export interface IOnSubmitRechargeValue {
  success: boolean;
  message?: string;
  order: Order;
  payment: Payment;
}

/**
 * Parâmetros para gravar a recarga no cartão após o pagamento.
 * Conta fechada: grava apenas o valor da recarga (ativação cobrada não vai no cartão).
 * Conta aberta: soma a recarga ao saldo e chama writeBalance.
 */
export interface IWriteRechargeToCardAfterPaymentParams {
  /** Pedido já pago (com totalValue e activationValue quando for abertura) */
  order: Order;
  /** Dados atuais do cartão (Balance em centavos; para conta aberta é o saldo atual) */
  cardData: IFullCardData;
  /** true = abertura de conta (cartão fechado), false = recarga em conta já aberta */
  isClosed: boolean;
}
